const httpLib = require('./http-library.js');
const publicConfig = require('../public-config.js');
const _ = require('lodash');

const bookingsHost = publicConfig.external_address;

// private

function _readBooking (bookingOrPublicID, token, callback) {
  const params = {
    host: bookingsHost,
    method: 'GET',
    path: `/v2/bookings/${bookingOrPublicID}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  if (!token) {
    httpLib.request(_.omit(params, 'headers'), (res) => callback(res));
  }

  httpLib.request(params, (res) => callback(res));
}

function _readBookingStatus (bookingOrPublicID, token, callback) {
  const params = {
    host: bookingsHost,
    method: 'GET',
    path: `/v2/bookings/${bookingOrPublicID}/status`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, (res) => callback(res));
}

function _updateBooking (bookingID, payload, token, callback, guestUpdate) {
  const params = {
    host: bookingsHost,
    method: 'PUT',
    path: !guestUpdate ? `/v2/bookings/${bookingID}` : `/v2/bookings/${bookingID}/guest/update`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, function (res) {
    callback(res);
  }, payload);
}

function _updateBookingAction (payload, token, path, callback) {
  const params = {
    host: bookingsHost,
    method: 'PUT',
    path: `/v2/bookings/${path}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, function (res) {
    callback(res);
  }, payload);
}

function _readBookingUpdate (bookingID, viewAs, token, callback) {
  const updateBy = 'host' === viewAs.toLowerCase() ? 'guest' : 'host';
  const params = {
    host: bookingsHost,
    method: 'GET',
    path: `/v2/bookings/${bookingID}/${updateBy}/update`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  httpLib.request(params, function (res) {
    callback(res);
  });
}

function _updateOffer (payload, token, path, callback) {
  const params = {
    host: bookingsHost,
    method: 'PUT',
    path: `/v2/bookings/${path}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, function (res) {
    callback(res);
  }, payload);
}


function _postInquiry (payload, token, callback) {
  const params = {
    host: bookingsHost,
    method: 'POST',
    path: '/v2/bookings/inquire',
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, function (res) {
    callback(res);
  }, payload);
}

function _postBooking (payload, token, path, callback) {
  const params = {
    host: bookingsHost,
    method: 'POST',
    path: `/v2/bookings${path}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, (res) => {
    callback(res);
  }, payload);
}

function _readOvertime (bookingID, token, callback) {
  const params = {
    host: bookingsHost,
    method: 'GET',
    path: `/v2/bookings/${bookingID}/host/overtime/status`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  httpLib.request(params, function (res) {
    callback(res);
  });
}

function _postOvertime (payload, token, bookingID, path, callback) {
  const params = {
    host: bookingsHost,
    method: 'POST',
    path: `/v2/bookings/${bookingID}/host/overtime${path}`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  httpLib.request(params, (res) => {
    callback(res);
  }, payload);
}

function _readCancellation (bookingID, token, callback) {
  const params = {
    host: bookingsHost,
    method: 'GET',
    path: `/v2/bookings/${bookingID}/guest/cancel`,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  httpLib.request(params, function (res) {
    callback(res);
  });
}

// public

const Bookings = {

  create: (payload, token, path, callback) => {
    const postBooking = _postBooking(payload, token, path, callback);
    return postBooking;
  },

  read: (bookingOrPublicID, token, callback) => {
    const readBooking = _readBooking(bookingOrPublicID, token, callback);
    return readBooking;
  },

  readStatus: (bookingOrPublicID, token, callback) => {
    const readBookingStatus = _readBookingStatus(bookingOrPublicID, token, callback);
    return readBookingStatus;
  },

  /**
   * Send update payload to Bookings API - `/v2/bookings/:booking_id` + `/guest/update`
   * if the update is coming from the guest
   *
   * @function
   * @name update
   * @param {string}   - Booking ID
   * @param {object}   - Update object
   * @param {string}   - Access token (retrived from sessions)
   * @param {function} - Callback
   * @param {boolean}  - Whether update is for guest meta fields
   */

  update: (bookingID, payload, token, callback, guestUpdate) => {
    const updateBooking = _updateBooking(bookingID, payload, token, callback, guestUpdate);
    return updateBooking;
  },

  /**
   * Send update request action to Bookings API
   * `/v2/bookings/:booking_id/host/update/deny`
   * `/v2/bookings/:booking_id/host/update/approve`
   * `/v2/bookings/:booking_id/guest/update/withdraw`
   *
   * @function
   * @param {object}   - Update object
   * @param {string}   - Access token (retrived from sessions)
   * @param {string}   - Path (one of three shown above after :booking_id)
   * @param {function} - Callback
   */

  updateAction: (payload, token, path, callback) => {
    const updateBookingAction = _updateBookingAction(payload, token, path, callback);
    return updateBookingAction;
  },

  readUpdate: (bookingID, viewAs, token, callback) => {
    const readBookingUpdate = _readBookingUpdate(bookingID, viewAs, token, callback);
    return readBookingUpdate;
  },

  /**
   * Send inquiry payload to Bookings API - `/v2/bookings/inquire`
   *
   * @function
   * @name inquire
   * @param {Object}   - Inquiry object
   * @param {String}   - Access token (retrived from sessions)
   * @param {Function} - Callback
   */

  inquire: (payload, token, callback) => {
    const postInquiry = _postInquiry(payload, token, callback);
    return postInquiry;
  },

  /**
   * Send inquiry payload to Bookings API - `/v2/bookings/inquire`
   *
   * @function
   * @name updateOffer
   * @param {String}   - id of the booking in quesiton
   * @param {Object}   - Inquiry object
   * @param {String}   - Access token (retrived from sessions)
   * @param {String}   - either /accept /deny /withdraw
   * @param {Function} - Callback
   */

  updateOffer: (payload, token, path, callback) => {
    const putOffer = _updateOffer(payload, token, path, callback);
    return putOffer;
  },

  /**
   * Send overtime payload to Bookings API
   *
   * @function
   * @name submitOvertime
   * @param {Object}   - Overtime object
   * @param {String}   - Access token (retrived from sessions)
   * @param {String}   - bookingID
   * @param {String}   - Path, either prepare_only=1 or '' to submit overtime
   * @param {Function} - Callback
   */
  submitOvertime: (payload, token, bookingID, path, callback) => {
    const putOffer = _postOvertime(payload, token, bookingID, path, callback);
    return putOffer;
  },

  /**
   * Read overtime status of a booking
   *
   * @function
   * @name readOvertime
   * @param {String}   - bookingID
   * @param {String}   - Access token (retrived from sessions)
   * @param {Function} - Callback
   */
  readOvertime: (bookingID, token, callback) => {
    const readOvertime = _readOvertime(bookingID, token, callback);
    return readOvertime;
  },
  /**
   * Read guest cancellation update of a booking
   *
   * @function
   * @name readCancellation
   * @param {String}   - bookingID
   * @param {String}   - Access token (retrived from sessions)
   * @param {Function} - Callback
   */
  readCancellation: (bookingID, token, callback) => {
    const readCancellation = _readCancellation(bookingID, token, callback);
    return readCancellation;
  }
};

module.exports = Bookings;
